import { useCallback, useState } from "react";
import type { DataTableRowEditParams } from "primereact/datatable";

type RowWithIdT = { id: string };

type UseRowEditingParamsT<T extends RowWithIdT> = {
  onSave?: (row: T) => void;
  onCancel?: (row: T) => void;
};

export const useRowEditing = <T extends RowWithIdT>({
  onSave,
  onCancel,
}: UseRowEditingParamsT<T> = {}) => {
  const [editingRows, setEditingRows] = useState<Record<string, boolean>>({});

  const isRowEditing = useCallback(
    (row: T) => Boolean(editingRows[row.id]),
    [editingRows],
  );

  const stopEditing = useCallback((row: T) => {
    setEditingRows((prev) => {
      const next = { ...prev };
      delete next[row.id];
      return next;
    });
  }, []);

  const startEditing = useCallback((row: T) => {
    setEditingRows((prev) => ({ ...prev, [row.id]: true }));
  }, []);

  const onRowEditChange = useCallback(({ data }: DataTableRowEditParams) => {
    setEditingRows(data as Record<string, boolean>);
  }, []);

  const saveRow = useCallback(
    (row: T) => {
      onSave?.(row);
      stopEditing(row);
    },
    [onSave, stopEditing],
  );

  const cancelRow = useCallback(
    (row: T) => {
      onCancel?.(row);
      stopEditing(row);
    },
    [onCancel, stopEditing],
  );

  return {
    editingRows,
    isRowEditing,
    startEditing,
    saveRow,
    cancelRow,
    onRowEditChange,
  };
};
